import React from 'react';
import { StandardTableActions } from '../../../components/ui';

import { Column } from '../../../components/DataTable/DataTable';
import { Manufacturer } from './useData';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';

export const moduleConfig = {
  module: {
    title: 'Manufacturers',
    description: 'Manage manufacturers',
    permissionModuleKey: 'manufacturer',
    route: {
      list: '/manufacturer',
      new: '/manufacturer/new',
      detail: (id: number) => `/manufacturer/${id}`,
      edit: (id: number) => `/manufacturer/${id}/edit`,
    },
    api: {
      list: '/manufacturers',
      detail: (id: number) => `/manufacturers/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: {
    placeholder: 'Search manufacturers...',
  },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['name'],
    },
  ],
  filters: {
    fields: [
      {
        key: 'name',
        label: 'Name',
        placeholder: 'Type to filter name...',
        type: 'text' as const,
        getValue: (item: Manufacturer) => item.name,
        options: [],
      },
    ],
    applyFilter: (items: Manufacturer[], filters: { name: string[] | string }) => {
      return items.filter((item) => {
        const nameFilter = filters.name;
        if (typeof nameFilter === 'string' && nameFilter.trim() !== '') {
          const value = (item.name || '').toLowerCase();
          if (!value.includes(nameFilter.toLowerCase())) return false;
        } else if (Array.isArray(nameFilter) && nameFilter.length > 0) {
          const value = (item.name || '').toLowerCase();
          const matches = nameFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }
        return true;
      });
    },
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Manufacturer',
        showTitle: false,
        variant: 'primary' as const,
        href: '/manufacturer/new',
      },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: Manufacturer) => {
      window.location.href = `/manufacturer/${item.id}`;
    },
    keyExtractor: (item: Manufacturer) => item.id,
  },
  emptyStates: {
    noData: 'Get started by creating a new manufacturer.',
    noSearchResults: 'No manufacturers found matching your search criteria.',
    noFilterResults: 'No manufacturers match the selected filters.',
  },
  delete: {
    title: 'Delete Manufacturer',
    message: 'Are you sure you want to delete this manufacturer? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete manufacturer. Please try again.',
    handler: async (id: number) => {
      const { deleteManufacturer } = await import('../../../services/mutations');
      await deleteManufacturer(id);
    },
  },
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },
  detail: {
    subtitle: 'Manufacturer Details',
    notFoundTitle: 'Manufacturer not found',
    notFoundMessage: 'The manufacturer you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Manufacturers',
    defaultTitle: 'Unnamed Manufacturer',
    getTitle: (item: Manufacturer) => {
      return item.name || `Manufacturer #${item.id}`;
    },
  },
  form: {
    new: {
      title: 'New Manufacturer',
      subtitle: 'Add a new manufacturer',
      submitText: 'Create Manufacturer',
    },
    edit: {
      title: 'Edit Manufacturer',
      subtitle: 'Update manufacturer information',
      submitText: 'Update Manufacturer',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading manufacturer...',
    errorMessage: 'Failed to save manufacturer. Please try again.',
    create: async (data: Partial<Manufacturer>) => {
      const { createManufacturer } = await import('../../../services/mutations');
      return await createManufacturer(data);
    },
    update: async (id: number, data: Partial<Manufacturer>) => {
      const { updateManufacturer } = await import('../../../services/mutations');
      return await updateManufacturer(id, data);
    },
  },
  fields: [
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Enter manufacturer name',
      validation: {
        required: true,
        minLength: 1,
        maxLength: 255,
      },
    },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Manufacturer Information',
        icon: '🏭',
        fields: ['name'],
        columns: 1,
        collapsible: false,
      },
    ],
  } as FormLayout,
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<Manufacturer>[] => [
  {
    key: 'id',
    header: 'ID',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
  {
    key: 'name',
    header: 'Name',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
];

export const getDetailSections = (
  item: Manufacturer,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '🏭',
      fields: [
        {
          label: 'Name',
          value: item.name || '-',
        },
      ],
    },
  ];
};

export const getStatusBadges = (item: Manufacturer) => {
  return null;
};

export const getInitialFormData = (): Partial<Manufacturer> => {
  return {
    name: '',
  };
};

export const mapItemToFormData = (item: Manufacturer): Partial<Manufacturer> => {
  return {
    name: item.name || '',
  };
};

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

export const getTableActions = (
  item: Manufacturer,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

